import { useEffect, useRef, useState } from 'react';

const LARGE_LOGO_HEIGHT = 96;
const SMALL_LOGO_HEIGHT = 48;
const LOGO_SHRINK_THRESHOLD = 60;
const HEADER_PADDING = 32;
const CTA_BAR_HEIGHT = 36;
const SMALL_HEADER_HEIGHT = CTA_BAR_HEIGHT + SMALL_LOGO_HEIGHT + HEADER_PADDING;

export const MAX_HEADER_HEIGHT = LARGE_LOGO_HEIGHT + HEADER_PADDING + CTA_BAR_HEIGHT;

interface UseHeaderHeightResult {
  headerTranslateY: number;
  isRevealing: boolean;
  logoHeight: number;
  ctaBarHeight: number;
}

export default function useHeaderHeight(): UseHeaderHeightResult {
  const [logoHeight, setLogoHeight] = useState(LARGE_LOGO_HEIGHT);
  const [headerTranslateY, setHeaderTranslateY] = useState(0);
  const [isRevealing, setIsRevealing] = useState(false);
  const lastScrollY = useRef(0);
  const scrollDownAnchor = useRef<number | null>(null);
  const ticking = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;

      // Smooth interpolation: logo shrinks linearly from 96px to 48px
      // as scrollY goes from 0 to LOGO_SHRINK_THRESHOLD (60)
      const clampedY = Math.min(Math.max(currentY, 0), LOGO_SHRINK_THRESHOLD);
      const t = clampedY / LOGO_SHRINK_THRESHOLD;
      setLogoHeight(LARGE_LOGO_HEIGHT - t * (LARGE_LOGO_HEIGHT - SMALL_LOGO_HEIGHT));

      // Header scroll-out / reveal logic
      if (currentY <= 0) {
        setHeaderTranslateY(0);
        setIsRevealing(false);
        scrollDownAnchor.current = null;
      } else if (currentY <= LOGO_SHRINK_THRESHOLD) {
        // Still in the logo-shrink zone — header stays fully visible
        setHeaderTranslateY(0);
        setIsRevealing(false);
        scrollDownAnchor.current = null;
      } else if (currentY > lastScrollY.current) {
        // Scrolling down past threshold — scroll header out naturally
        if (scrollDownAnchor.current === null) {
          scrollDownAnchor.current = lastScrollY.current;
        }
        const offset = Math.min(currentY - scrollDownAnchor.current, SMALL_HEADER_HEIGHT);
        setHeaderTranslateY(-offset);
        setIsRevealing(false);
      } else {
        // Scrolling up — reveal header with animation
        setHeaderTranslateY(0);
        setIsRevealing(true);
        scrollDownAnchor.current = null;
      }

      lastScrollY.current = currentY;
      ticking.current = false;
    };
    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(handleScroll);
        ticking.current = true;
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return {
    headerTranslateY,
    isRevealing,
    logoHeight,
    ctaBarHeight: CTA_BAR_HEIGHT,
  }
}
